/**
 * Add Context dialog: place entity nodes from the picker at the next
 * viewport slot. File uploads route through the shared ingestion pipeline
 * via CanvasInsertMenu.
 */

import {
  DEFAULT_FILE_NODE_HEIGHT,
  DEFAULT_FILE_NODE_WIDTH,
  getEntityMediaDisplayBox,
} from "@/pages/canvas/utils/imageDimensions";
import type { Entity } from "@cohesive/ui-components";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasNodePlacement } from "./useCanvasNodePlacement";

export function useCanvasAddContextPlacement() {
  const { addNode } = useCanvas();
  const { getNextNodePosition, getEntityNodeWidth, getEntityNodeHeight } =
    useCanvasNodePlacement();

  const getWebpagePlacement = useCallback(
    () => ({
      position: getNextNodePosition(
        DEFAULT_FILE_NODE_WIDTH,
        DEFAULT_FILE_NODE_HEIGHT,
      ),
      width: DEFAULT_FILE_NODE_WIDTH,
      height: DEFAULT_FILE_NODE_HEIGHT,
    }),
    [getNextNodePosition],
  );

  const handleSelectEntity = useCallback(
    (entity: Entity) => {
      const nodeW = getEntityNodeWidth(entity);
      const nodeH = getEntityNodeHeight(entity);
      const position = getNextNodePosition(nodeW, nodeH);

      const persistFittedMediaDims = getEntityMediaDisplayBox(entity) != null;

      const newNode = {
        id: uuidv4(),
        type: "entity",
        position,
        ...(persistFittedMediaDims ? { width: nodeW, height: nodeH } : {}),
        data: {
          entity,
        },
      };

      addNode(newNode);
    },
    [addNode, getNextNodePosition, getEntityNodeWidth, getEntityNodeHeight],
  );

  return { handleSelectEntity, getWebpagePlacement };
}
